import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';

import { Auth, User as FirebaseUser, OAuthProvider, UserCredential as FirebaseUserCredential, linkWithPopup, signInWithCredential, signInWithPopup } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { LoginUser, MotesPaths } from '../models';

@Injectable({ providedIn: 'root' })
export class AppleLoginService {

    constructor(private http: HttpClient, private auth: Auth) {
    }

    /**
     * Get credential by signing in with pop-up.
     * Link with current user if anonymous and not already linked.
     */
    public async signInWithPopup(currentUser: FirebaseUser): Promise<FirebaseUserCredential> {
        // Create Apple provider
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');

        let credential: Promise<FirebaseUserCredential>;

        // If current user is anonymous, link with new user:
        // Even though this user will not be used, we want it to override the anonymous user,
        // so that we end up having 2 and not 3 users for each new Apple login.
        if(currentUser && currentUser.isAnonymous) {
            // Handle errors from caller
            credential = linkWithPopup(currentUser, provider);
        }
        // Otherwise, sign in regularly
        else credential = signInWithPopup(this.auth, provider);
        return credential;
    }

    /**
     * Convert a Apple authorization code into a custom Firebase login token
     * @param authorizationCode Apple authorization code from Apple OAuth login session
     * @returns Custom firebase token 
     */
    public async getCustomAppleToken(authorizationCode: string): Promise<string> {
        // Request custom firebase token from StoryHunt API
        const getTokenReq = this.http.post<{ customToken: string, error?: string }>(MotesPaths.MOTES_REST + "/v1/auth/apple-token", {
            'authorizationCode': authorizationCode
        });
        const json = await firstValueFrom(getTokenReq).catch(err => err as { customToken?, error: string });

        if(!json || json.error) {
            console.warn("Could not get custom token from API", json)
            return "";
        }

        return json.customToken;
    }

    /**
     * Convert a Firebase Apple tokenId into a custom Firebase login token (with same UID as on native login)
     * @param firebaseToken Firebase Apple id token from Apple OAuth login session
     * @returns Custom firebase token 
     */
    public async getCustomFirebaseToken(firebaseToken: string): Promise<string> {
        // Request custom firebase token from StoryHunt API
        const getTokenReq =  this.http.post<{ customToken: string, error?: string }>(MotesPaths.MOTES_REST + "/v1/auth/firebase-token", {
            'idToken': firebaseToken
        });
        const json = await firstValueFrom(getTokenReq).catch(err => err as { customToken?, error: string });

        if(!json || json.error) {
            console.warn("Could not get custom token from API", json)
            return "";
        }
        return json.customToken;
    }

    /**
     * Merge Apple provider data with login credentials to form a LoginCredential object with firebase auth token and user info
     * @param loginUser LoginUser with provider data (email, profile image etc.)
     * @param firebaseCredentials firebase credentials obtained by logging in with custom token or other means (it probably only contains tokens and no user info other than email)
     * @returns 
     */
    public async getLoginCredentials(loginUser: LoginUser, firebaseCredentials: FirebaseUserCredential): Promise<FirebaseUserCredential> {
        return {
            // Get providerId, operationType etc from Firebase credentials
            ...firebaseCredentials,
            user: {
                // Get refreshToken, getIdTokenResult() etc from Firebase login.
                ...firebaseCredentials.user,
                // Get user info: providerData, email, photoURL etc. from LoginUser object (from Native App etc.)
                ...loginUser,
                // Make sure uid comes from firebase credentials
                uid: firebaseCredentials.user.uid,
                // Copy / reference id token result function from real Firebase credentials
                getIdTokenResult: firebaseCredentials.user.getIdTokenResult,
                getIdToken: firebaseCredentials.user.getIdToken,
                reload: firebaseCredentials.user.reload,
                toJSON: firebaseCredentials.user.toJSON,
            }
        };
    }

    /**
     * Merge Firebase Apple provider data with login credentials to form a LoginCredential object with firebase auth token and user info
     * @param userInfo FirebaseCredentials with provider data, email etc. (and UID associated with the wrong user)
     * @param customCredentials Custom credentials used to authenticate towards Firebase (with UID that fits with the Native App user)
     * @returns Firebase user credentials
     */
    public async getLoginCredentialsFirebase(userInfo: FirebaseUserCredential, customCredentials: FirebaseUserCredential): Promise<FirebaseUserCredential> {
        return {
            // Get providerId, operationType etc from Firebase credentials
            ...userInfo,
            user: {
                // Get refreshToken, getIdTokenResult() etc.
                ...customCredentials.user,
                // Get user info: providerData, email, photoURL etc. from userInfo (from Native App etc.)
                ...userInfo.user,
                // Make sure uid comes from firebase credentials
                uid: customCredentials.user.uid,
                // Copy / reference id token result function from real Firebase credentials
                getIdTokenResult: customCredentials.user.getIdTokenResult,
                getIdToken: customCredentials.user.getIdToken,
                reload: customCredentials.user.reload,
                toJSON: customCredentials.user.toJSON,
            }
        };
    }
}
